import { BUDGET_OPTIONS, SERVICE_OPTIONS } from '@/constants/landing'

// Shared by the inquiry form (instant feedback) and /api/contact (the source of truth).

export type InquiryField = 'name' | 'email' | 'company' | 'services' | 'budget' | 'message'

export type InquiryErrors = Partial<Record<InquiryField, string>>

export interface Inquiry {
    name: string
    email: string
    company: string
    services: Array<string>
    budget: string
    message: string
}

export const INQUIRY_FIELDS: Array<InquiryField> = ['name', 'email', 'company', 'services', 'budget', 'message']

export const INQUIRY_LIMITS = {
    name: 100,
    email: 254,
    company: 120,
    messageMin: 20,
    messageMax: 4000
}

const EMAIL_PATTERN = /^[^\s@"<>()[\]\\,;:]+@[^\s@"<>()[\]\\,;:]+\.[^\s@"<>()[\]\\,;:]{2,}$/

const toLine = (value: unknown): string =>
    typeof value === 'string' ? value.replace(/\s+/g, ' ').trim() : ''

const toText = (value: unknown): string =>
    typeof value === 'string' ? value.replace(/\r\n?/g, '\n').replace(/\n{3,}/g, '\n\n').trim() : ''

export const hasErrors = (errors: InquiryErrors): boolean => Object.values(errors).some(Boolean)

export const validateInquiry = (input: unknown): { inquiry: Inquiry, errors: InquiryErrors } => {
    const source = typeof input === 'object' && input !== null ? input as Record<string, unknown> : {}
    const pickedServices: Array<unknown> = Array.isArray(source.services) ? source.services : []

    const inquiry: Inquiry = {
        name: toLine(source.name),
        email: toLine(source.email),
        company: toLine(source.company),
        services: SERVICE_OPTIONS.filter(option => pickedServices.includes(option)),
        budget: toLine(source.budget),
        message: toText(source.message)
    }

    const errors: InquiryErrors = {}

    if (inquiry.name.length < 2) errors.name = 'Tell us your name.'
    else if (inquiry.name.length > INQUIRY_LIMITS.name) errors.name = `Keep your name under ${INQUIRY_LIMITS.name} characters.`

    if (!inquiry.email) errors.email = 'We need an email address to reply to.'
    else if (inquiry.email.length > INQUIRY_LIMITS.email || !EMAIL_PATTERN.test(inquiry.email)) errors.email = "That email address doesn't look quite right."

    if (inquiry.company.length > INQUIRY_LIMITS.company) errors.company = `Keep the company name under ${INQUIRY_LIMITS.company} characters.`

    if (inquiry.budget && !BUDGET_OPTIONS.includes(inquiry.budget)) errors.budget = 'Pick one of the budget ranges.'

    if (inquiry.message.length < INQUIRY_LIMITS.messageMin) errors.message = `Give us a little more to go on, at least ${INQUIRY_LIMITS.messageMin} characters.`
    else if (inquiry.message.length > INQUIRY_LIMITS.messageMax) errors.message = "Keep it under 4,000 characters. We'll dig into the rest on a call."

    return { inquiry, errors }
}
